import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    LIST, ADD, EDIT,
    ASC,
} from "const"
import { MtStudy, MtStudySession } from "server/models"
import { StateType, deepCopy, hasProjectAccess } from "client/utils"
import {
    PRM_STUDY_SESSION,
    StoreType,
    StudyTeamMemberList,
    StudyTeamMemberListByStudy,
    StudySessionList,
    StudySessionListByStudy,
    StudySessionFilters,

    LoadAllStudies,
    LoadAllStudyTeamMembers,
    LoadAllStudySessions,
    LoadAllStudySessionAttendance,
    DeleteStudySession,
    SortStudies,
    SortStudyTeamMembers,
    SortStudySessions,

    setStudyList,
    setStudyIds,
    setStudyTeamMemberList,
    setStudyTeamMemberListByStudy,
    setStudySessionList,
    setStudySessionListByStudy,
    setStudySessionAttendance,
    setStudySessionIds,
    setStudySessionSort,
    setStudySessionFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import StudySessionSearch from "client/pages/search/studySessionSearch"
import Columns from "./columns"
import SessionAttendance from "./attendance"


const List = () => {
    const baseRoute = useSelector((state: StoreType) => state.studySession.baseRoute)
    const sessionList = useSelector((state: StoreType) => state.studySession.sessionList)
    const sessionIds = useSelector((state: StoreType) => state.studySession.sessionIds)
    const sortColumn = useSelector((state: StoreType) => state.studySession.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.studySession.sortOrder)
    const filters = useSelector((state: StoreType) => state.studySession.filters)
    const reloadList = useSelector((state: StoreType) => state.studySession.reloadList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadDataState, setLoadDataState] = useState<StateType>(IDLE)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        setLoadDataState(PENDING)
        Promise.all([
            LoadAllStudies(project.id).then((studyList) => {
                const studies: MtStudy[] = Object.values(studyList)
                const studyIds = SortStudies(studies, "name", ASC, {})
                dispatch(setStudyList(studyList))
                dispatch(setStudyIds(studyIds))

                let teamMemberList: StudyTeamMemberList = {}
                const teamMemberListByStudy: StudyTeamMemberListByStudy = {}

                const nestedPromiseArray: Promise<any>[] = []
                studies.forEach((study) => {
                    nestedPromiseArray.push(
                        LoadAllStudyTeamMembers(study.id).then((res) => {
                            teamMemberList = {
                                ...teamMemberList,
                                ...res,
                            }
                            const teamMemberIds = SortStudyTeamMembers(Object.values(res), "name", ASC, {})
                            teamMemberListByStudy[study.id] = teamMemberIds
                        }),
                    )
                })

                return Promise.all(nestedPromiseArray).then(() => {
                    dispatch(setStudyTeamMemberList(teamMemberList))
                    dispatch(setStudyTeamMemberListByStudy(teamMemberListByStudy))
                })
            }),
            LoadAllStudySessionAttendance(project.id).then((res) => {
                dispatch(setStudySessionAttendance(res))
            }),
        ]).then(() => {
            setLoadDataState(IDLE)
        })
    }, [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllStudySessions(project.id).then((res) => {
            const sessionListByStudy: StudySessionListByStudy = {}
            const sortedSessionIds = SortStudySessions(Object.values(res), "date", ASC, {})
            sortedSessionIds.forEach((sessionId) => {
                const session = res[sessionId]
                if (!sessionListByStudy[session.studyId]) sessionListByStudy[session.studyId] = []
                sessionListByStudy[session.studyId].push(sessionId)
            })

            dispatch(setStudySessionList(res))
            dispatch(setStudySessionListByStudy(sessionListByStudy))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortStudySessions(Object.values(sessionList), sortColumn, sortOrder, filters)
        dispatch(setStudySessionIds(sortedIds))
    }, [sessionList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtStudySession, order) => {
        dispatch(setStudySessionSort(column, order))
    }

    const onFilter = (newFilters: StudySessionFilters) => {
        dispatch(setStudySessionFilters(newFilters))
    }

    const onDelete: DeleteRow = (session: MtStudySession, callback) => {
        DeleteStudySession(session.id, project.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const sessionListCopy: StudySessionList = sessionList ? deepCopy(sessionList) : {}
                delete sessionListCopy[session.id]
                dispatch(setStudySessionList(sessionListCopy))
                dispatch(setStudySessionIds(sessionIds.filter((id) => id !== session.id)))
            })
        })
    }

    const columns = Columns()

    const listAccess = hasProjectAccess(PRM_STUDY_SESSION, LIST)
    const addAccess = hasProjectAccess(PRM_STUDY_SESSION, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.sessions}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (loadDataState === PENDING || loadState === PENDING)
                    && <Loader />
                }
                <TableSearchWrapper>
                    <StudySessionSearch
                        id="sessionList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({
                                    url: `${baseRoute}/${EDIT}/0`,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={sessionList}
                    dataIds={sessionIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </PageContent>

            <SessionAttendance />
        </Container>
    )
}
export default List
